/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import GenericModal from "../../Modal/GenericModal";
import { Vocabulary } from "../../Utils/Vocabulary";
import {
  ChangeEvent,
  SyntheticEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import { updateData } from "../../Services/updateData";
import { postData } from "../../Services/postData";
import { urlEnum } from "../../Utils/urlEnum";
import { AdministrativeLocation, UserData } from "./UsersDashboard";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { CacheContext } from "../../Context/cacheContext";
import { ToastContainer, toast } from "react-toastify";
import { getActiveLocations } from "../../Utils/utils";
import {
  Visibility,
  Security,
  SyncAlt,
} from "@mui/icons-material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { GenericModel } from "../../Models/Models";
import TrimesterSyncDialog, { MonthYear } from "./TrimesterSyncDialog";

type UserProps = {
  open: boolean;
  title: string;
  visibilityUser: boolean;
  editedUser: UserData | null;
  onClose: (shouldRefetch?: boolean) => void;
};

const initialState: UserData = {
  name: "",
  telefon: "",
  email: "",
  locatie_administrativa: {
    id: "",
    nume: "",
  },
  locatie_administrativa_id: [],
  main_location_id: null,
  comision_user: 0,
  base_net_salary: "",
  is_managing_users: false,
  password: "",
  confirmPassword: "",
  roles: [],
  is_subcontractor: false,
  two_factor_enabled: false,
};
export default function User(props: UserProps) {
  const notifyError = (message: string) => toast.error(message);
  const { open, title, visibilityUser, editedUser, onClose } = props;
  const [changePassword, setChangePassword] = useState(false);
  const [data, setData] = useState<UserData>(initialState);
  const [showPassword, setShowPassword] = useState(false);
  const [locationChanged, setLocationChanged] = useState(false);
  const [bonusSyncDialogOpen, setBonusSyncDialogOpen] = useState(false);
  const cacheContext = useContext(CacheContext) as any;

  useEffect(() => {
    ValidatorForm.addValidationRule("isPasswordMatch", (value) => {
      if (value !== data.password) return false;
      return true;
    });

    if (editedUser) {
      const newUserData = Object.assign({}, editedUser);
      setData(newUserData);
    } else setData(initialState);
    setLocationChanged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedUser]);

  /**
   *
   * @param event
   */
  function handleChangeTextFields(event: any) {
    setData({ ...data, [event.target.name]: event.target.value });
    ValidatorForm.addValidationRule("isPasswordMatch", (value) => {
      if (value !== data.password) return false;
      return true;
    });
  }

  /**
   *
   * @param event
   * @param newValue
   */
  function handleChangeLocation(_: any, newValue: AdministrativeLocation[]) {
    const newIds = newValue
      ? newValue.map((loc: AdministrativeLocation) => loc.id)
      : [];
    const originalIds = editedUser?.locatie_administrativa_id ?? [];
    const hasChanged =
      newIds.length !== originalIds.length ||
      newIds.some((id) => !originalIds.includes(id));

    if (editedUser && hasChanged) setLocationChanged(true);

    setData((prev) => ({ ...prev, locatie_administrativa_id: newIds }));
  }

  function handleChangeMainLocation(
    _: any,
    newValue: AdministrativeLocation | null,
  ) {
    const newId = newValue ? newValue.id : null;
    if (editedUser && newId !== editedUser.main_location_id)
      setLocationChanged(true);
    setData((prev) => ({ ...prev, main_location_id: newId }));
  }

  /**
   * Get selected locations for display
   */
  const getSelectedLocations = () => {
    if (
      !data.locatie_administrativa_id ||
      !Array.isArray(data.locatie_administrativa_id)
    ) {
      return [];
    }
    return (
      cacheContext.cache?.locations.filter((location: AdministrativeLocation) =>
        data.locatie_administrativa_id.includes(location.id),
      ) || []
    );
  };

  /**
   *
   * @param event
   * @param newValue
   */
  function handleChangeRoles(event: any, newValue: any) {
    const newData = Object.assign({}, data);
    if (newValue) newData.roles = newValue;
    else newData.roles = [];
    setData(newData);
  }
  function validateForm(): boolean {
    if (
      !data.locatie_administrativa_id ||
      (Array.isArray(data.locatie_administrativa_id) &&
        data.locatie_administrativa_id.length === 0)
    ) {
      notifyError(Vocabulary.selectLocation);
      return false;
    }
    if (data.roles && data.roles.length === 0) {
      notifyError(Vocabulary.selectRoles);
      return false;
    }
    return true;
  }

  function saveUser() {
    if (!validateForm()) return;
    if (editedUser && locationChanged) {
      setBonusSyncDialogOpen(true);
      return;
    }
    performSave(null);
  }

  function performSave(syncMonths: MonthYear[] | null) {
    const roles = data?.roles?.map((role: any) => role.id);
    const newUser: any = {
      name: data.name,
      telefon: data.telefon,
      email: data.email,
      locatie_administrativa_id: data.locatie_administrativa_id,
      main_location_id: data.main_location_id,
      base_net_salary: data.base_net_salary,
      comision_user: data.comision_user,
      managed_user_ids: data?.managed_user_ids,
      is_managing_users: data?.is_managing_users,
      roles,
      confirmPassword: data.confirmPassword,
      is_subcontractor: data.is_subcontractor,
      two_factor_enabled: data.two_factor_enabled,
    };

    if (syncMonths && syncMonths.length > 0) newUser.trimester_sync_months = syncMonths;

    if (editedUser) {
      if (changePassword) newUser.password = data.password;
      updateData(`${urlEnum.users}/${editedUser.id}`, newUser)
        .then((response) => {
          cacheContext.setCache({
            ...cacheContext.cache,
            users: response.data.users,
          });
          setData(initialState);
          onClose(true);
        })
        .catch(() => {
          setData(initialState);
          onClose(false);
        });
    } else {
      newUser.password = data.password;
      postData(urlEnum.users, newUser)
        .then((response) => {
          cacheContext.setCache({
            ...cacheContext.cache,
            users: response.data.users,
          });
          setData(initialState);
          onClose(true);
        })
        .catch(() => {
          setData(initialState);
          onClose(false);
        });
    }
  }

  /**
   *
   * @returns values for autocomplete from url
   */
  const getValuesForMultipleAutocomplete = () => {
    const array: number[] = [];
    if (data?.managed_user_ids) {
      if (data?.managed_user_ids?.length) {
        let foundedObject = null;
        data?.managed_user_ids?.forEach((user: number) => {
          foundedObject = cacheContext.cache?.users.find(
            (type: GenericModel) => {
              return type.id == user;
            },
          );
          if (foundedObject) array.push(foundedObject);
        });
      }
    }
    return array && array.length ? array : [];
  };

  /**
   *
   *
   */
  const handleChangeMultipleAutocomplete = (
    event: SyntheticEvent<Element, Event>,
    newValue: number[],
  ) => {
    const newData = Object.assign({}, data);
    if (newValue) newData.managed_user_ids = newValue;
    else newData.managed_user_ids = [];
    setData(newData);
  };

  return (
    <>
      <ToastContainer />
      <GenericModal
        open={open}
        onClose={() => onClose(false)}
        title={title}
        buttons={
          !visibilityUser ? (
            <Box sx={{ display: "flex", gap: 2, width: "100%", px: 1, pb: 1 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={() => onClose(false)}
              >
                {Vocabulary.cancel}
              </Button>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                form="user-form"
                style={{ color: "white" }}
              >
                {Vocabulary.save}
              </Button>
            </Box>
          ) : undefined
        }
      >
        <ValidatorForm id="user-form" onSubmit={saveUser}>
          <Grid container spacing={2} sx={{ mt: 0.5, mb: 1 }}>
            {/* ── Informații personale ── */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: "0.68rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {Vocabulary.personalInfo}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextValidator
                fullWidth
                name="name"
                validators={["required"]}
                errorMessages={[Vocabulary.requiredField]}
                label={Vocabulary.userName}
                value={data.name}
                disabled={visibilityUser}
                onChange={handleChangeTextFields}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Autocomplete
                id="roles"
                multiple
                freeSolo={false}
                disablePortal
                disabled={visibilityUser}
                getOptionLabel={(option: any) => option.name}
                getOptionDisabled={(option) =>
                  !!(
                    data?.roles &&
                    data.roles.find((el: any) => el.name === option.name)
                  )
                }
                value={data.roles}
                options={cacheContext.cache?.roles}
                onChange={(_: any, newValue: any) =>
                  handleChangeRoles(_, newValue)
                }
                sx={{ width: "100%", zIndex: 10000 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Vocabulary.roles}
                    variant="outlined"
                    disabled={visibilityUser}
                    error={data.roles?.length === 0}
                    helperText={data.roles?.length === 0 && Vocabulary.required}
                  />
                )}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextValidator
                fullWidth
                name="telefon"
                validators={["required"]}
                errorMessages={[Vocabulary.requiredField]}
                label={Vocabulary.phone}
                value={data.telefon}
                disabled={visibilityUser}
                onChange={handleChangeTextFields}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextValidator
                type="email"
                fullWidth
                name="email"
                validators={["required", "isEmail"]}
                errorMessages={[
                  Vocabulary.requiredField,
                  Vocabulary.incorrectEmail,
                ]}
                label={Vocabulary.email}
                value={data.email}
                disabled={visibilityUser}
                onChange={handleChangeTextFields}
              />
            </Grid>

            {/* ── Salarizare ── */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: "0.68rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {Vocabulary.salary}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextValidator
                fullWidth
                type="number"
                name="base_net_salary"
                errorMessages={[Vocabulary.requiredField]}
                label={Vocabulary.baseNetSalary}
                value={data.base_net_salary ?? ""}
                disabled={visibilityUser}
                onChange={handleChangeTextFields}
              />
            </Grid>

            {/* ── Locații ── */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: "0.68rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {Vocabulary.locationSection}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </Grid>
            {locationChanged && (
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  icon={<SyncAlt fontSize="inherit" />}
                  sx={{
                    borderRadius: "8px",
                    boxSizing: "border-box",
                    width: "100%",
                    "& .MuiAlert-message": {
                      overflow: "hidden",
                      wordBreak: "break-word",
                    },
                  }}
                >
                  {Vocabulary.locationChangedBonusNotice}
                </Alert>
              </Grid>
            )}
            <Grid item md={6} xs={12}>

              <Autocomplete
                id="locatie_administrativa_id"
                freeSolo={false}
                disablePortal
                multiple
                disabled={visibilityUser}
                getOptionLabel={(option: any) => option.nume}
                options={getActiveLocations(cacheContext.cache?.locations)}
                value={getSelectedLocations()}
                onChange={(_: any, newValue: any) =>
                  handleChangeLocation(_, newValue)
                }
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="locatie_administrativa"
                    label={Vocabulary.locationVehicles}
                    variant="outlined"
                    disabled={visibilityUser}
                    error={
                      !data.locatie_administrativa_id ||
                      (Array.isArray(data.locatie_administrativa_id) &&
                        data.locatie_administrativa_id.length === 0)
                    }
                    helperText={
                      (!data.locatie_administrativa_id ||
                        (Array.isArray(data.locatie_administrativa_id) &&
                          data.locatie_administrativa_id.length === 0)) &&
                      Vocabulary.required
                    }
                  />
                )}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Autocomplete
                id="main_location_id"
                freeSolo={false}
                disablePortal
                disabled={visibilityUser}
                getOptionLabel={(option: any) => option.nume}
                options={getActiveLocations(cacheContext.cache?.locations)}
                value={
                  cacheContext.cache?.locations?.find(
                    (loc: AdministrativeLocation) =>
                      loc.id == data.main_location_id,
                  ) || null
                }
                onChange={handleChangeMainLocation}
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="main_location"
                    label={Vocabulary.mainLocation}
                    variant="outlined"
                    disabled={visibilityUser}
                  />
                )}
              />
            </Grid>

            {/* ── Securitate ── */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: "0.68rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {Vocabulary.security}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </Grid>
            <Grid item md={6} xs={12}>
              {editedUser ? (
                <TextValidator
                  fullWidth
                  type={!showPassword ? "password" : "text"}
                  name="password"
                  label={Vocabulary.password}
                  validators={[
                    changePassword ? "required" : "matchRegexp:^.{8,100}$",
                    "matchRegexp:^.{8,100}$",
                  ]}
                  errorMessages={[
                    Vocabulary.requiredField,
                    "Minim 8 caractere",
                  ]}
                  error={
                    data.password && data.confirmPassword !== data.password
                  }
                  helperText={
                    data.password &&
                    data.confirmPassword !== data.password &&
                    Vocabulary.passwordNoMatch
                  }
                  value={data.password}
                  disabled={!changePassword}
                  onChange={handleChangeTextFields}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Checkbox
                          onClick={() => setChangePassword(!changePassword)}
                          checked={changePassword}
                          disabled={visibilityUser}
                        />
                        <span
                          style={{
                            fontSize: 12,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div>{"Schimba"}</div>
                          <div style={{ marginTop: -10 }}>{"parola"}</div>
                        </span>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <TextValidator
                  fullWidth
                  type={!showPassword ? "password" : "text"}
                  name="password"
                  label={Vocabulary.password}
                  validators={["required", "matchRegexp:^.{8,100}$"]}
                  errorMessages={[
                    Vocabulary.requiredField,
                    "Minim 8 caractere",
                    Vocabulary.passwordNoMatch,
                  ]}
                  error={
                    data.password && data.confirmPassword !== data.password
                  }
                  helperText={
                    data.password &&
                    data.confirmPassword !== data.password &&
                    Vocabulary.passwordNoMatch
                  }
                  value={data.password}
                  disabled={visibilityUser}
                  onChange={handleChangeTextFields}
                />
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              {(changePassword || !editedUser) && (
                <TextValidator
                  fullWidth
                  type={!showPassword ? "password" : "text"}
                  name="confirmPassword"
                  label={Vocabulary.confirmPassword}
                  validators={[
                    "required",
                    "matchRegexp:^.{0,100}$",
                    "isPasswordMatch",
                  ]}
                  errorMessages={[
                    Vocabulary.requiredField,
                    "Minim 8 caractere",
                    Vocabulary.passwordNoMatch,
                  ]}
                  error={
                    data.password && data.confirmPassword !== data.password
                  }
                  helperText={
                    data.password &&
                    data.confirmPassword !== data.password &&
                    Vocabulary.passwordNoMatch
                  }
                  disabled={visibilityUser}
                  value={data.confirmPassword}
                  onChange={handleChangeTextFields}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disabled={visibilityUser}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {!showPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  borderRadius: "12px",
                  background: data.two_factor_enabled
                    ? "linear-gradient(135deg, rgba(237, 108, 2, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)"
                    : "linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.04) 100%)",
                  border: data.two_factor_enabled
                    ? "1px solid rgba(255, 152, 0, 0.3)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s ease",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Security
                    sx={{
                      fontSize: 28,
                      color: data.two_factor_enabled ? "#ed6c02" : "#9e9e9e",
                      transition: "color 0.3s ease",
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        fontWeight: 500,
                        color: "#333",
                      }}
                    >
                      {Vocabulary.twoFactorAuthActivate}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
                      {Vocabulary.twoFactorAuthDescription}
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={data.two_factor_enabled}
                  disabled={visibilityUser}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setData({
                      ...data,
                      two_factor_enabled: event.target.checked,
                    })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#ed6c02" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#ff9800",
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* ── Setări cont ── */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: "0.68rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {Vocabulary.accountSettings}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </Grid>
            <Grid item md={6} xs={12}>
              <FormControlLabel
                sx={{ marginLeft: "-7px" }}
                control={
                  <Checkbox
                    checked={data.is_managing_users}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setData({
                        ...data,
                        is_managing_users: event.target.checked,
                      })
                    }
                  />
                }
                label={
                  <Typography sx={{ fontSize: "1rem" }}>
                    {Vocabulary.manageOtherUsers}
                  </Typography>
                }
              />
              {data?.is_managing_users && (
                <Autocomplete
                  id="managed_user_ids"
                  freeSolo={false}
                  fullWidth
                  disablePortal
                  multiple
                  getOptionLabel={(option: any) => option?.nume}
                  isOptionEqualToValue={(option: any, value) =>
                    value ? option.id === value.id : false
                  }
                  getOptionDisabled={(option) => option?.id == editedUser?.id}
                  size="small"
                  value={getValuesForMultipleAutocomplete()}
                  options={cacheContext.cache?.users}
                  onChange={(event: any, newValue: any) => {
                    const ids = newValue.map((v: GenericModel) => v.id);
                    handleChangeMultipleAutocomplete(event, ids);
                  }}
                  sx={{ mt: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={Vocabulary.user}
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <FormControlLabel
                sx={{ marginLeft: "-7px" }}
                control={
                  <Checkbox
                    checked={data.is_subcontractor}
                    disabled={visibilityUser}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setData({
                        ...data,
                        is_subcontractor: event.target.checked,
                      })
                    }
                  />
                }
                label={
                  <Typography sx={{ fontSize: "1rem" }}>
                    {Vocabulary.subcontractor}
                  </Typography>
                }
              />
            </Grid>
          </Grid>

        </ValidatorForm>
      </GenericModal>

      <TrimesterSyncDialog
        open={bonusSyncDialogOpen}
        onClose={() => setBonusSyncDialogOpen(false)}
        onConfirm={(syncOption) => {
          setBonusSyncDialogOpen(false);
          performSave(syncOption);
        }}
      />
    </>
  );
}
